import type { Component, ParentComponent } from 'solid-js'
/* eslint-disable ts/no-use-before-define */
import { createLazyMemo } from '@solid-primitives/memo'
import { MetaProvider, Title } from '@solidjs/meta'
import { HashRouter, Navigate, Route, useIsRouting, useLocation, useParams, useSearchParams } from '@solidjs/router'
import { Logger } from 'besonders-logger'
import classNames from 'classnames'
import capitalize from 'lodash-es/capitalize'
import { createEffect, createMemo, createResource, createSignal, lazy, on, onCleanup, Show, Suspense, untrack } from 'solid-js'
import { appInit, formatGlobalError, globalError, initialized, setGlobalError, setInitialized } from './appInit'
import { BackLink } from './components/BackLink'
import { ExtraSearchResults, HeaderSearchBar } from './components/bars/SearchBar'
import { TopBar } from './components/bars/TopBar'
import { BlockSettingsDialog } from './components/BlockSettings'
import { RecordingOverlay } from './components/RecordingOverlay'
import { IconifyButton, RedirectWorkaround, Spinner } from './components/mini-components'
import { PwaReloadPrompt } from './components/PwaReloadPrompt'
import { getApplogDB, initApplogDB } from './data/ApplogDB'
import { SearchContext, useExtraSearchResults } from './data/search'
import { AgentSetupPage, shouldShowAgentSetup } from './pages/AgentSetupPage'
import { Help } from './pages/HelpPage'
import { InspectPage } from './pages/InspectPage'
import { useAppSettings } from './ui/app-settings'

import { onlyFromCurrentAgent } from './data/agent/current-agent'
import { ThreadContext, useCurrentThread } from './ui/reactive'
import { createDebouncedWithEqCheck, replaceUriParts, useSingleUrlParam } from './ui/utils-ui'
import './data/devtools-helpers'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO, { prefix: '[App]' }) // eslint-disable-line unused-imports/no-unused-vars

// import MainPage from './MainPage'
// import SettingsPage from './SettingsPage'
// import TestPage from './TestPage'
const MainPage = lazy(async () => ({ default: (await import('./pages/MainPage')).MainPage }))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const [searchOpen, setSearchOpen] = createSignal(false) // TODO: refactor to search provider

export const [blockSettingsID, setBlockSettingsID] = createSignal('')
export function showBlockSettings(blkID) {
	setBlockSettingsID(blkID)
}
export function hideBlockSettings() {
	setBlockSettingsID('')
}

export const AppRoot: Component = () => {
	// const isPreview = createMemo(() => (window.location.hash.includes('preview=') || window.location.hash.includes('search='))) // HACK

	const homeBlockUrl = createLazyMemo(() => { // lazy so the errors are not thrown
		DEBUG('calc homeBlockUrl', initialized()) // only if url is explicitly #/home
		if (!initialized()) return null
		const appSettings = useAppSettings(onlyFromCurrentAgent(useCurrentThread(getApplogDB())))
		if (!appSettings.homeBlock) throw ERROR(`missing appsetting: homeBlockUrl`, appSettings)
		if (!appSettings.homeBlock) {
			WARN(`missing appsetting: homeBlockUrl`, appSettings)
			return '/timeline'
		}
		return `/focus/${appSettings.homeBlock}`
	})

	return (
		<HashRouter root={App}>
			<Route
				path='/'
				component={StartPageRedirect}
			/>
			<Route
				path='/home'
				component={() => (
					<Show when={homeBlockUrl()}>
						<Navigate href={homeBlockUrl()} />
					</Show>
				)}
			/>
			<Route
				path='/block/:blockID'
				component={() => {
					const params = useParams()
					const location = useLocation()
					return <Navigate href={`/focus/${params.blockID}${location.search}`} />
				}}
			/>
			<Route
				path={['/new', '/timeline', '/focus/:blockID']}
				component={() => {
					const location = useLocation()
					const [searchParams] = useSearchParams()
					const needsAgentSetup = createMemo(() => !searchParams.preview && !searchParams.zen && shouldShowAgentSetup())
					return (
						<Show
							when={!needsAgentSetup()}
							fallback={<Navigate href={`/setup?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`} />}
						>
							<MainPage searchOpen={searchOpen} />
						</Show>
					)
				}}
			/>

			<Route
				path={'/settings/*section'}
				component={() => <SettingsPage />}
			/>
			<Route
				path='/help'
				component={() => <Help />}
			/>
			<Route
				path='/setup/*section'
				component={AgentSetupPage}
			/>
			<Route
				path='/inspect'
				component={InspectPage}
			/>

			<Route
				path='*'
				component={() => (
					<div w-full>
						<BackLink />
						<h1>404 - page not found</h1>
					</div>
				)}
			/>
		</HashRouter>
	)
}
const StartPageRedirect: Component = () => {
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const isPreviewOrSearch = createMemo(() => !!searchParams.preview || !!searchParams.search)
	// const isPreview = createMemo(() => (window.location.hash.includes('preview=') || window.location.hash.includes('search='))) // HACK

	const redirectUrl = createMemo(() => {
		if (!initialized()) return null

		const appSettings = useAppSettings()
		const startPage: string = appSettings.startPage
		let newPathname
		// if (isPreviewOrSearch) newPathname = '/timeline'
		// else {
		DEBUG({ startPage, appSettings })
		if (startPage === 'home-block') {
			newPathname = '/home'
		} else if (startPage === 'empty-block') {
			newPathname = '/new'
		} else if (startPage === 'timeline') {
			newPathname = '/timeline'
		} else {
			WARN(`unknown startPage (using it either way): `, startPage)
			newPathname = startPage
		}
		// }
		return replaceUriParts(location, { pathname: newPathname }) // to keep preview= & co
	})
	return (
		<>
			{!isPreviewOrSearch()
				? (
					<Show when={redirectUrl()}>
						{/* <Navigate href={redirectUrl()} /> Sadly broken... */}
						<RedirectWorkaround url={redirectUrl()} />
					</Show>
				)
				: (
					<Suspense fallback={<Spinner size='3rem' color='green' />}>
						<MainPage searchOpen={searchOpen} />
					</Suspense>
				)}
		</>
	)
}

let renderCount = 0
export const App: ParentComponent = (props) => {
	;(renderCount++ > 0 ? WARN : DEBUG)('!!! APP RENDER !!!', renderCount)
	// trace(true)

	const [searchParams] = useSearchParams()
	const pubFromUrl = createMemo(() => searchParams.share || searchParams.pub)
	const location = useLocation()
	// const navigate = useNavigate()
	const isRouting = useIsRouting()
	const isPreview = createMemo(() => !!searchParams.preview)
	const isZenview = createMemo(() => !!searchParams.zen)
	// const isZenview = createMemo(() => location.pathname.startsWith('/zenview'))

	const [searchInUrl, setSearchInUrl] = useSingleUrlParam('search')
	const [search, setSearch] = createSignal(untrack(() => searchInUrl()))

	//////////
	// INIT //
	//////////
	const cleanup = new Set<() => void>()
	const [applogDB] = createResource(async () => {
		LOG('[App] Initializing DB')
		const db = await initApplogDB()
		LOG('[App] DB init done', db)
		const cleanUp = await appInit({ pubFromUrl: untrack(() => pubFromUrl()) })
		cleanup.add(cleanUp)
		LOG('[App] cleanup ready', cleanup)
		return db
	})
	const initialized = () => !!applogDB()
	// sync global signal for external consumers (TopBar, mini-components)
	createEffect(() => setInitialized(initialized()))
	createEffect(() => {
		const err = applogDB.error
		if (err) {
			ERROR('[App] startup failed', err)
			setGlobalError({
				source: 'appInit',
				timestamp: new Date().toISOString(),
				message: err?.message ?? String(err),
				name: err?.name,
				stack: err?.stack,
			})
		}
	})
	onCleanup(() => {
		for (const eachCleanup of cleanup) {
			eachCleanup()
		}
	})

	const setSearchInUrlDebounced = createDebouncedWithEqCheck(
		searchInUrl, // will only update if still the same after debounce
		(newSearch: string) => {
			DEBUG(`debounced setSearchInUrl:`, newSearch)
			setSearchInUrl(
				newSearch,
				// { replace: !!searchParams.search == !!newSearch } // we debounce already, so... new entry
			)
		},
		1400, // ? after which time of having a search entry should it have it's own separate history entry?
	)
	createEffect(on(() => searchInUrl(), (searchInUrl) => {
		DEBUG(`searchInUrl changed to`, searchInUrl, { searchInState: search() })
		if (searchInUrl !== search()) {
			// changed from browser navigation
			setSearch(searchInUrl || null)
			setSearchOpen(!!searchInUrl)
		}
	}))
	createEffect(on(() => search(), (search) => {
		DEBUG(`search changed to`, search)
		// if (!search) setSearchOpen(false)
		if (search !== null && search !== undefined) {
			setSearchInUrlDebounced(search)
		}
	}))
	createEffect(on(() => searchOpen(), (searchOpen) => {
		DEBUG(`searchOpen changed to`, searchOpen, { search: search() })
		if (!searchOpen) {
			setSearch(null)
			setSearchInUrl(null)
			setSearchInUrlDebounced.cancel()
		}
	}))
	const searchResults = createMemo(() => {
		if (!applogDB()) return null
		const results = useExtraSearchResults({ search$: search, thread: applogDB() })()
		DEBUG(`<App>.searchResults`, results)
		return results
	})

	const title = createMemo(() => {
		const location = useLocation()
		const pathname = createMemo(() => location.pathname.slice(1))
		DEBUG({ pathname: pathname() })
		if (searchOpen()) return `Search '${search()}'`
		// Block routes set their own <Title> from MainPage where displayThread (preview-aware) is in scope
		if (pathname().startsWith('block') || pathname().startsWith('focus')) return null
		if (pathname()) return `${capitalize(pathname())}`
		return null
	})

	const updateSchoelaceDarkmode = (enabled: boolean) => document.documentElement.classList.toggle('wa-theme-dark', enabled)
	const prefersDarkQuery = window.matchMedia('(prefers-color-scheme: dark)')
	updateSchoelaceDarkmode(window.matchMedia && prefersDarkQuery.matches)
	prefersDarkQuery.addEventListener('change', (event) => {
		LOG(`Dark mode`, event.matches)
		updateSchoelaceDarkmode(event.matches)
	})

	LOG('App rendering', location.pathname)
	return (
		<ThreadContext.Provider value={applogDB}>
			<SearchContext.Provider value={{ search, setSearch }}>
				<MetaProvider>
					<Title>{title() ? `${title()} - ` : ''}Note3</Title>
					{/* <OverlaySearchBar open={searchOpen} setOpen={setSearchOpen} /> */}
					<Show
						when={initialized()}
					>
						{/* <Show when={!!blockSettingsID()}> */}
						<BlockSettingsDialog blockID={blockSettingsID()} />
						{/* </Show> */}
					</Show>

					<RecordingOverlay />

					<Show when={isRouting()}>
						<div absolute inset-0 bg='black opacity-40' flex='~ col items-center justify-center ' z-1000>
							{/* (i) workaround for https://github.com/solidjs/solid-router/discussions/296 */}
							<Spinner size='3rem' color='red' m-12 />
							{
								/* <div animate='property-[opacity] fade-in duration-[3s]' text-red>
								stuck? <a href='#' underline visited:color-inherit onClick={() => window.location.reload()}>reload</a>
							</div> */
							}
						</div>
					</Show>

					<Show when={globalError()}>
						<div fixed top-2 left-2 right-2 z-2000 max-w-4xl mx-auto>
							<wa-callout variant='danger' open>
								<div flex='~ items-center gap-2' mb-1>
									<div class='i-ph:warning-bold' />
									<strong>{globalError()!.source}</strong>
									<wa-copy-button
										ml-auto
										value={formatGlobalError(globalError()!)}
										copy-label='Copy all error info'
										success-label='Copied'
										error-label='Copy failed'
									/>
									<IconifyButton
										appearance='plain'
										variant='neutral'
										icon='x-bold'
										size='l'
										hint='Dismiss'
										onClick={() => setGlobalError(null)}
									/>
								</div>
								<pre whitespace-pre-wrap font-mono text-xs m-0>{globalError()!.message}</pre>
								<Show when={globalError()!.cause}>
									<details mt-2>
										<summary cursor-pointer text-xs>Cause</summary>
										<pre whitespace-pre-wrap font-mono text-xs mt-1>{globalError()!.cause}</pre>
									</details>
								</Show>
								<Show when={globalError()!.extra}>
									<details mt-2>
										<summary cursor-pointer text-xs>Extra</summary>
										<pre whitespace-pre-wrap font-mono text-xs mt-1>{globalError()!.extra}</pre>
									</details>
								</Show>
								<Show when={globalError()!.stack}>
									<details mt-2>
										<summary cursor-pointer text-xs>Stack trace</summary>
										<pre whitespace-pre-wrap font-mono text-xs mt-1>{globalError()!.stack}</pre>
									</details>
								</Show>
							</wa-callout>
						</div>
					</Show>

					<div
						id='main-container'
						flex='~ col items-center justify-start'
						class='min-h-100vh bg-basebg color-text'
						px-2
						border-box
						h-100vh
						overflow-y-scroll
						overflow-x-clip
					>
						<div
							flex='~ col items-stretch justify-start'
							class={classNames(['box-border max-w-4xl min-h-100vh w-full bg-basebg color-text', isZenview() ? 'pt-4' : ''])}
							gap-2
						>
							<Show when={!isZenview()}>
								<div my-2>
									<div
										id='header-search-wrapper'
										class={searchOpen() ? 'flex' : 'hidden'}
										w-full
										flex='items-center justify-between'
										relative
										gap-2
									>
										{
											/* <h2 m-0 flex='inline items-center gap-4'>
											<BackLink text='' onClick={() => setSearchOpen(false)} />
										</h2> */
										}
										<HeaderSearchBar open={searchOpen()} setOpen={setSearchOpen} firstResult={searchResults()?.[0]} />
									</div>
									<TopBar searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
								</div>
							</Show>

							<PwaReloadPrompt class='w-full' />

							<Show
								when={initialized()}
								fallback={
									<div flex='~ justify-center' w-full>
										{/* <pre>innit? {JSON.stringify(initialized())}</pre> */}
										<Spinner size='3rem' color='purple' m-12 />
									</div>
								}
							>
								<Show when={searchResults()?.length}>
									<ExtraSearchResults results={searchResults()} close={() => setSearchOpen(false)} />
								</Show>
								<Show when={!searchResults() || searchResults().length === 0}>
									<Suspense
										fallback={
											<div flex='~ col items-center justify-start'>
												<Spinner size='3rem' color='green' m-12 />
											</div>
										}
									>
										{props.children /* Routes */}
									</Suspense>
								</Show>
							</Show>
						</div>
					</div>
				</MetaProvider>
			</SearchContext.Provider>
		</ThreadContext.Provider>
	)
}
